import { api } from './api';
import type { Office } from './offices';
import type { Agency } from './agencies';

export interface LeaderboardEvent {
  id: number;
  name: string;
  startDate: string;
  endDate: string;
  milestone: number;
  clientMilestone: number;
  reviewMilestone: number;
  isActive: boolean;
}

export interface LeaderboardEventsResponse {
  events: LeaderboardEvent[];
  currentEvent: LeaderboardEvent;
}

export interface LeaderboardEventSummary {
  eventName: string;
  eventStartDate: string;
  eventEndDate: string;
  milestone: number;
  reviewMilestone: number;
  clientMilestone: number;
  isActive: boolean;
  totalParticipants: number;
  totalHours: number;
  totalClients: number;
  reviewsCount: number;
}

export interface LeaderboardEventError {
  message: string[];
  error: string;
  statusCode: number;
}

export const leaderboardEvents = {
  getList: async (): Promise<LeaderboardEventsResponse> => {
    try {
      const response = await api.get<LeaderboardEventsResponse>('/leaderboard-events');
      return response;
    } catch (error) {
      const leaderboardError = error as LeaderboardEventError;
      throw leaderboardError;
    }
  },

  getSummary: async (eventId: number): Promise<LeaderboardEventSummary> => {
    try {
      const response = await api.get<LeaderboardEventSummary>(
        `/leaderboard-events/${eventId}/summary`
      );
      return response;
    } catch (error) {
      const leaderboardError = error as LeaderboardEventError;
      throw leaderboardError;
    }
  },

  getAgencies: async (eventId: number): Promise<Agency[]> => {
    try {
      const response = await api.get<Agency[]>(`/leaderboard-events/${eventId}/agencies`);
      return response;
    } catch (error) {
      const leaderboardError = error as LeaderboardEventError;
      throw leaderboardError;
    }
  },
  getParticipantReviews: async (
    eventId: number,
    participantId: number
  ): Promise<ParticipantReview[]> => {
    try {
      const response = await api.get<ParticipantReview[]>(
        `/leaderboard-events/${eventId}/participants/${participantId}/reviews`
      );
      return response;
    } catch (error) {
      const leaderboardError = error as LeaderboardEventError;
      throw leaderboardError;
    }
  },

  deleteParticipantReview: async (
    eventId: number,
    participantId: number,
    reviewId: number
  ): Promise<void> => {
    try {
      await api.delete(
        `/leaderboard-events/${eventId}/participants/${participantId}/reviews/${reviewId}`
      );
    } catch (error) {
      const leaderboardError = error as LeaderboardEventError;
      throw leaderboardError;
    }
  },
  getParticipants: async (
    eventId: number,
    params?: {
      limit?: number;
      offset?: number;
      userName?: string;
      dateFrom?: string;
      dateTo?: string;
    }
  ): Promise<ParticipantsResponse> => {
    try {
      const response = await api.get<ParticipantsResponse>(
        `/leaderboard-events/${eventId}/participants`,
        { params }
      );
      return response;
    } catch (error) {
      const leaderboardError = error as LeaderboardEventError;
      throw leaderboardError;
    }
  },
  createClient: async (
    eventId: number,
    participantId: number,
    data: CreateClientPayload
  ): Promise<void> => {
    try {
      await api.post(`/leaderboard-events/${eventId}/participants/${participantId}/clients`, data);
    } catch (error) {
      const leaderboardError = error as LeaderboardEventError;
      throw leaderboardError;
    }
  },
  getParticipantClients: async (
    eventId: number,
    participantId: number
  ): Promise<ParticipantClient[]> => {
    try {
      const response = await api.get<ParticipantClient[]>(
        `/leaderboard-events/${eventId}/participants/${participantId}/clients`
      );
      return response;
    } catch (error) {
      const leaderboardError = error as LeaderboardEventError;
      throw leaderboardError;
    }
  },

  deleteParticipantClient: async (
    eventId: number,
    participantId: number,
    clientId: number
  ): Promise<void> => {
    try {
      await api.delete(
        `/leaderboard-events/${eventId}/participants/${participantId}/clients/${clientId}`
      );
    } catch (error) {
      const leaderboardError = error as LeaderboardEventError;
      throw leaderboardError;
    }
  },

  addParticipantReviews: async (
    eventId: number,
    participantId: number,
    data: CreateReviewsPayload
  ): Promise<void> => {
    try {
      await api.post(`/leaderboard-events/${eventId}/participants/${participantId}/reviews`, data);
    } catch (error) {
      const leaderboardError = error as LeaderboardEventError;
      throw leaderboardError;
    }
  },

  getParticipantOffices: async (eventId: number, participantId: number): Promise<Office[]> => {
    try {
      const response = await api.get<Office[]>(
        `/leaderboard-events/${eventId}/participants/${participantId}/offices`
      );
      return response;
    } catch (error) {
      const leaderboardError = error as LeaderboardEventError;
      throw leaderboardError;
    }
  },

  getOfficesByAgencyId: async (eventId: number, agencyId: number): Promise<Office[]> => {
    try {
      const response = await api.get<Office[]>(
        `/leaderboard-events/${eventId}/agencies/${agencyId}/offices`
      );
      return response;
    } catch (error) {
      const leaderboardError = error as LeaderboardEventError;
      throw leaderboardError;
    }
  },
};

export interface LeaderboardParticipant {
  id: number;
  rank: number;
  firstName: string;
  lastName: string;
  totalHours: number;
  totalScore: number;
  totalClients: number;
  reviewsCount: number;
  districtNames: string | null;
  officeNames: string | null;
  agencyNames: string | null;
  lastUpdated: string;
}

export interface ParticipantsResponse {
  pageNumber: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
  participants: LeaderboardParticipant[];
  myParticipant: LeaderboardParticipant | null;
}

export interface CreateClientPayload {
  hours: number;
  clientId?: number | null;
  firstName?: string | null;
  lastName?: string | null;
  caregiverName?: string | null;
  startOfCareDate?: string | null;
  referralDate?: string | null;
  agencyId?: number | null;
  officeId?: number | null;
}

export interface ParticipantClient {
  id: number;
  hours: number;
  clientId: number | null;
  firstName: string | null;
  lastName: string | null;
  startOfCareDate: string | null;
  referralDate: string | null;
  officeName: string | null;
  districtName: string | null;
  agencyName: string | null;
}

export interface CreateReviewsPayload {
  officeId: number;
  quantityOfReviews: number;
}

export interface ParticipantReview {
  id: number;
  quantityOfReviews: number;
  addedAt: string;
  officeName: string | null;
  districtName: string | null;
}
